/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect, useCallback, useRef } from 'react'
import { Typography, Box, Button } from '@mui/material'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import moment from 'moment'
import mimeTypes from 'mime'
import axios from 'axios'

import { useMultiState } from '../../hooks'
import AuthContext from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'

import eventByIdQuery from '../../graphql/query/eventById'
import organizersQuery from '../../graphql/query/organizers'
import updateEventMutation from '../../graphql/mutation/editEvent'
import Breadcrumb from '../../components/BreadcrumbLayout'

// Import components
import {
  EventDetailsForm,
  ShirtSection,
  TicketSection,
  FeatureToggles,
} from '../../components/edit-event'

const initialState = {
  eventNameEN: '',
  slugEvent: '',
  organizer: '',
  codeEvent: '',
  logo: '',
  startDate: '',
  endDate: '',
  QRSearch: false,
  IDSearch: false,
  isFaceCheckin: false,
  isShirtImage: false,
  uploadFace: false,
  IDFace: false,
  print: false,
}

const EditEventPage = () => {
  const formRef = useRef(null)
  const [file, setFile] = useState({})
  const [shirts, setShirt] = useState([])
  const [slugShirt, setSlugShirt] = useState('')
  const [typeShirt, setTypeShirt] = useState('')
  const [imageShirt, setImageShirt] = useState('')
  const [tickets, setTickets] = useState([])
  const [ticketType, setTicketType] = useState('')
  const [dateTicket, setDateTicket] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [timeDate, setTimeDate] = useState(false)
  const { eventId } = useParams()
  const [state, setState] = useMultiState(initialState)
  const { user } = useContext(AuthContext)
  const { data: data2 } = useQuery(organizersQuery)
  const { data, refetch: refetchEvent } = useQuery(eventByIdQuery, {
    variables: { _id: eventId },
    fetchPolicy: 'network-only',
  })

  const addShirt = useCallback(() => {
    const newShirt = {
      slug: slugShirt,
      type: typeShirt,
      image: imageShirt,
    }
    console.log(newShirt, 'newShirt')
    setShirt((oldShirts) => [...oldShirts, newShirt])
    setSlugShirt('')
    setImageShirt('')
    setTypeShirt('')
  }, [slugShirt, typeShirt, imageShirt])

  const addTicketType = useCallback(() => {
    const newTicket = {
      ticketType,
      dateTicket,
      startDate,
      endDate,
    }
    setTickets((oldTickets) => [...oldTickets, newTicket])
    setTicketType('')
    setDateTicket('')
    setStartDate('')
    setEndDate('')
  }, [ticketType, dateTicket, startDate, endDate])

  const removeShirt = useCallback((index) => {
    setShirt((oldShirts) => [...oldShirts.slice(0, index), ...oldShirts.slice(index + 1)])
  }, [])

  const removeTicket = useCallback((index) => {
    setTickets((oldTickets) => [...oldTickets.slice(0, index), ...oldTickets.slice(index + 1)])
  }, [])

  const [updateEvent] = useMutation(updateEventMutation)
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()
  const reader = new FileReader()

  useEffect(() => {
    if (!data?.eventId) return
    const ev = data.eventId
    const opts = ev.options || {}
    const search = opts.search || {}
    const face = opts.face || {}

    setState({
      eventNameEN: ev.name ?? '',
      slugEvent: ev.slug ?? '',
      organizer: ev.organizerId ?? '',
      logo: ev.logo ?? '',
      codeEvent: ev.code ?? '',
      QRSearch: search.qrcode ?? false,
      IDSearch: search.idcard ?? false,
      isFaceCheckin: ev.isFaceCheckin ?? false,
      isShirtImage: ev.isShirtImage ?? false,
      uploadFace: face.upload ?? false,
      IDFace: face.idcard ?? false,
      print: opts.print ?? false,
      selfCheckin: opts.selfCheckin ?? false,
      autoPickup: opts.autoPickup ?? false,
    })

    // shirts and tickets from API are arrays
    const shirtsArray = Array.isArray(ev.shirts) ? ev.shirts : []
    setShirt(
      shirtsArray.map((item) => ({
        _id: item._id,
        slug: item.slug ?? '',
        image: item.image ?? '',
        type: item.type ?? '',
      }))
    )

    const ticketsArray = Array.isArray(ev.tickets) ? ev.tickets : []
    setTickets(
      ticketsArray.map((item) => ({
        _id: item._id,
        ticketType: item.ticketType ?? '',
        dateTicket: item.dateTicket ?? '',
        startDate: item.startDate ?? '',
        endDate: item.endDate ?? '',
      }))
    )
  }, [data, setState])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await updateEvent({
        variables: {
          record: {
            _id: eventId,
            name: state.eventNameEN,
            organizerId: state.organizer,
            slug: state.slugEvent,
            code: state.codeEvent,
            logo: state.logo,
            isFaceCheckin: state.isFaceCheckin,
            isShirtImage: state.isShirtImage,
            updatedById: user._id,
            options: {
              search: {
                qrcode: state.QRSearch,
                idcard: state.IDSearch,
              },
              face: {
                upload: state.uploadFace,
                idcard: state.IDFace,
              },
              print: state.print,
              selfCheckin: state.selfCheckin,
              autoPickup: state.autoPickup,
            },
            shirts,
            tickets,
          },
        },
      })
      await refetchEvent()
      showSuccess('Event updated successfully!', 'Update Successful')
      setTimeout(() => {
        navigate(`/event/${eventId}/participant`)
      }, 1500)
    } catch (err) {
      console.error('Error updating event:', err)
      const errorMessage =
        err.message ||
        err.graphQLErrors?.[0]?.message ||
        'Failed to update event. Please try again.'
      showError(errorMessage, 'Update Failed')
    }
  }

  const selectFile = async (e) => {
    e.preventDefault()
    const tempFile = e.currentTarget.files[0]
    setFile(tempFile)
    console.log(tempFile, 'sesese')
    reader.readAsDataURL(tempFile)
    reader.onload = async (event) => {
      const extType = mimeTypes.getExtension(tempFile.type)
      const ext = mimeTypes.getType(extType)
      const response = await fetch(process.env.REACT_APP_UPLOAD_SERVER, {
        method: 'POST',
        body: JSON.stringify({
          ext: `${ext}`,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const result = await response.json()
      await axios
        .put(result.uploadUri, tempFile, {
          headers: {
            'Content-Type': `${ext}`,
            'x-goog-acl': 'public-read',
          },
        })
        .then((e) => {
          setState({ logo: result.downloadUri })
        })
    }
  }

  if (user.role === 'Staff') {
    return 'Cannot Access'
  }

  const handleStartDate = (e) => {
    e.preventDefault()
    const dateStart = e.target.value
    console.log(e.target.value)
    if (moment(dateStart).isAfter(endDate)) {
      setTimeDate(true)
    } else {
      setTimeDate(false)
    }
    setStartDate(dateStart)
  }

  const handleEndDate = (e) => {
    const dateEnd = e.target.value
    if (moment(dateEnd).isBefore(startDate)) {
      setTimeDate(true)
    } else {
      setTimeDate(false)
    }
    setEndDate(dateEnd)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const droppedFile = e.dataTransfer?.files?.[0]
    if (droppedFile && droppedFile.type?.startsWith('image/')) {
      setFile(droppedFile)
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        pb: { xs: 3, sm: 4, md: 5 },
      }}>
      <Breadcrumb path="/events" titleLink="Event" titleTypo={state.eventNameEN || 'Edit Event'} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <Box sx={{ pb: { xs: 10, sm: 11, md: 12 } }}>
          {/* Page Header */}
          <Box sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                mb: { xs: 0.25, sm: 0.375, md: 0.5 },
                mt: { xs: 1.25, sm: 1.5, md: 2 },
                color: 'text.primary',
                fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.75rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}>
              Edit Event
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                lineHeight: 1.5,
              }}>
              Update event details below
            </Typography>
          </Box>

          {/* Event Details Section */}
          <EventDetailsForm
            eventNameEN={state.eventNameEN}
            organizer={state.organizer}
            slugEvent={state.slugEvent}
            codeEvent={state.codeEvent}
            file={file}
            existingLogo={state.logo}
            organizersData={data2}
            userRole={user.role}
            onEventNameChange={(e) => setState({ eventNameEN: e.target.value })}
            onOrganizerChange={(e) => setState({ organizer: e.target.value })}
            onSlugChange={(e) => setState({ slugEvent: e.target.value })}
            onCodeChange={(e) => setState({ codeEvent: e.target.value })}
            onFileSelect={selectFile}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />

          {/* Shirt Section */}
          <ShirtSection
            shirts={shirts}
            slugShirt={slugShirt}
            typeShirt={typeShirt}
            imageShirt={imageShirt}
            onSlugChange={(e) => setSlugShirt(e.target.value)}
            onTypeChange={(e) => setTypeShirt(e.target.value)}
            onImageChange={(e) => setImageShirt(e.target.value)}
            onAdd={addShirt}
            onRemove={removeShirt}
          />

          {/* Ticket Section */}
          <TicketSection
            tickets={tickets}
            ticketType={ticketType}
            startDate={startDate}
            endDate={endDate}
            timeDateError={timeDate}
            onTicketTypeChange={(e) => setTicketType(e.target.value)}
            onStartDateChange={handleStartDate}
            onEndDateChange={handleEndDate}
            onAdd={addTicketType}
            onRemove={removeTicket}
          />

          {/* Feature Toggles Section */}
          <FeatureToggles
            print={state.print}
            QRSearch={state.QRSearch}
            IDSearch={state.IDSearch}
            uploadFace={state.uploadFace}
            isFaceCheckin={state.isFaceCheckin}
            IDFace={state.IDFace}
            selfCheckin={state.selfCheckin}
            isShirtImage={state.isShirtImage}
            autoPickup={state.autoPickup}
            onPrintChange={(e) => setState({ print: e.target.checked })}
            onQRSearchChange={(e) => setState({ QRSearch: e.target.checked })}
            onIDSearchChange={(e) => setState({ IDSearch: e.target.checked })}
            onUploadFaceChange={(e) => setState({ uploadFace: e.target.checked })}
            onIsFaceCheckinChange={(e) => setState({ isFaceCheckin: e.target.checked })}
            onIDFaceChange={(e) => setState({ IDFace: e.target.checked })}
            onSelfCheckinChange={(e) => setState({ selfCheckin: e.target.checked })}
            onIsShirtImageChange={(e) => setState({ isShirtImage: e.target.checked })}
            onAutoPickupChange={(e) => setState({ autoPickup: e.target.checked })}
          />
        </Box>
      </form>

      {/* Fixed Submit Button - Full Width */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.98)' : 'rgba(18, 18, 18, 0.98)',
          backdropFilter: 'blur(10px)',
          borderTop: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 -2px 8px rgba(0, 0, 0, 0.1)'
              : '0 -2px 8px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
        }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            if (formRef.current) formRef.current.requestSubmit()
          }}
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            textTransform: 'none',
            py: { xs: 0.875, sm: 1, md: 1.25 },
            px: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 2px 6px rgba(52, 103, 246, 0.3)'
                : '0 2px 6px rgba(144, 202, 249, 0.4)',
            transition: (theme) =>
              theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                duration: theme.transitions.duration.short,
              }),
            '&:hover': {
              backgroundColor: 'primary.dark',
              boxShadow: (theme) =>
                theme.palette.mode === 'light'
                  ? '0 3px 10px rgba(52, 103, 246, 0.4)'
                  : '0 3px 10px rgba(144, 202, 249, 0.5)',
              transform: 'translateY(-1px)',
            },
          }}>
          Save Changes
        </Button>
      </Box>
    </Box>
  )
}

export default EditEventPage
